<template>
  <div class="row">
    <div class="col-md-12">
      <h3>
        <a role="button" class="me-2" @click="goBack">
          <i class="material-icons">arrow_back</i>
        </a>
        {{ toolDisplayName }}
      </h3>
    </div>
  </div>

  <div class="row">
    <div class="col-md-12 mb-3">
      <card :title="$t('tools.geoTraces')">
        <div class="d-flex flex-wrap align-items-center gap-3 mb-3 range-picker-row">
          <div class="d-flex align-items-center gap-2">
            <label class="form-label mb-0 me-1">{{ $t('tools.dateRange') }}</label>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              :unlink-panels="!isMobileViewport"
              range-separator="—"
              :start-placeholder="$t('common.startDate') || 'Start'"
              :end-placeholder="$t('common.endDate') || 'End'"
              format="DD/MM/YYYY"
              value-format="YYYY-MM-DD"
              :shortcuts="rangeShortcuts"
              :clearable="false"
              :editable="false"
              placement="bottom-start"
              popper-class="tool-geotraces-date-popper"
              @change="onFiltersChange"
            />
          </div>

          <el-radio-group v-model="sourceFilter" size="default" @change="onFiltersChange">
            <el-radio-button :value="'All'">
              <i class="material-icons trace-filter-icon">all_inclusive</i>
              {{ $t('tools.geoTraceFilterAll') }}
            </el-radio-button>
            <el-radio-button :value="'Reservations'">
              <i class="material-icons trace-filter-icon">qr_code_2</i>
              {{ $t('tools.geoTraceFilterReservations') }}
            </el-radio-button>
            <el-radio-button :value="'Device'">
              <i class="material-icons trace-filter-icon">sensors</i>
              {{ $t('tools.geoTraceFilterDevice') }}
            </el-radio-button>
          </el-radio-group>

          <span class="text-muted text-sm ms-auto">
            {{ traces.length }} {{ $t('tools.geoTraces').toLowerCase() }}
          </span>
        </div>

        <div v-if="tool && tool.deviceId" class="d-flex flex-wrap align-items-center gap-2 mb-3">
          <device-status-badge :last-contact="tool.deviceLastContact" />
          <span v-if="deviceSummaryText" class="text-sm text-muted">{{ deviceSummaryText }}</span>
        </div>

        <div v-if="loading" class="text-muted">{{ $t('common.loading') || 'Loading...' }}</div>
        <div v-else-if="traces.length === 0" class="text-muted">{{ $t('tools.noGeoTraces') }}</div>
        <template v-else>
          <div class="row geotraces-layout">
            <!-- Map: top on mobile, right on desktop -->
            <div class="col-12 col-lg-8 order-1 order-lg-2 mb-3 mb-lg-0">
              <div id="toolGeoTracesMap" class="map map-big"></div>
            </div>
            <!-- List: below on mobile, left on desktop -->
            <div class="col-12 col-lg-4 order-2 order-lg-1">
              <ul class="list-group trace-list">
                <li
                  v-for="t in traces"
                  :key="`${t.source}-${t.id}`"
                  class="list-group-item d-flex align-items-center trace-row"
                  :class="{ active: isActive(t) }"
                  @mouseenter="highlightTrace(t)"
                  @click="highlightTrace(t)"
                >
                  <span class="trace-icon me-3" :class="iconClassFor(t)">
                    <i class="material-icons">{{ iconFor(t) }}</i>
                  </span>
                  <div class="flex-grow-1 min-width-0">
                    <div class="fw-semibold text-truncate">{{ titleFor(t) }}</div>
                    <div class="text-sm text-muted text-truncate">{{ subtitleFor(t) }}</div>
                    <div class="text-sm">
                      {{ formatTimestamp(t.timestamp) }}
                      &middot;
                      {{ t.latitude.toFixed(5) }}, {{ t.longitude.toFixed(5) }}
                    </div>
                  </div>
                  <a
                    role="button"
                    class="ms-2"
                    :title="$t('tools.showOnMap') || 'Show on map'"
                    @click.stop="focusOnTrace(t)"
                  >
                    <i class="material-icons">place</i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </template>
      </card>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import Card from '@/components/Card.vue';
import DeviceStatusBadge, { isInContact, appLocale } from '@/components/DeviceStatusBadge.vue';
import { ElDatePicker, ElRadioGroup, ElRadioButton } from 'element-plus';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Source enum mirrors the backend: 1=ToolReservation, 2=ExternalToolReservation, 3=Device
const SOURCE = { ToolReservation: 1, ExternalToolReservation: 2, Device: 3 };

export default {
  name: 'ToolGeoTraces',
  components: { Card, DeviceStatusBadge, ElDatePicker, ElRadioGroup, ElRadioButton },
  data() {
    const toIsoDate = d => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);
    return {
      toolId: this.$route.params.toolId,
      tool: null,
      traces: [],
      loading: true,
      map: null,
      markers: {},
      activeTraceKey: null,
      sourceFilter: 'All',
      isMobileViewport: window.innerWidth < 768,
      dateRange: [toIsoDate(monthAgo), toIsoDate(today)],
      rangeShortcuts: [
        {
          text: this.$t('tools.rangeLast7Days') || 'Last 7 days',
          value: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 7);
            return [start, end];
          },
        },
        {
          text: this.$t('tools.rangeLast30Days') || 'Last 30 days',
          value: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 30);
            return [start, end];
          },
        },
        {
          text: this.$t('tools.rangeLast90Days') || 'Last 90 days',
          value: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 90);
            return [start, end];
          },
        },
        {
          text: this.$t('tools.rangeLastYear') || 'Last year',
          value: () => {
            const end = new Date();
            const start = new Date();
            start.setFullYear(start.getFullYear() - 1);
            return [start, end];
          },
        },
      ],
    };
  },
  computed: {
    toolDisplayName() {
      return this.tool ? this.tool.name : '';
    },
    deviceSummaryText() {
      const deviceTraces = this.traces.filter(t => t.source === SOURCE.Device && t.timestamp);
      if (deviceTraces.length === 0) return '';
      const newest = deviceTraces.reduce((a, b) => (new Date(a.timestamp) > new Date(b.timestamp) ? a : b));
      const date = this.formatTimestamp(newest.timestamp);
      // Devices only report a new position when they move, so with a live
      // device an old last fix means "stationary", not "no signal"
      return isInContact(this.tool?.deviceLastContact)
        ? this.$t('tools.stationarySince', { date })
        : this.$t('tools.lastKnownLocation', { date });
    },
  },
  async mounted() {
    await Promise.all([this.fetchTool(), this.fetchTraces()]);
    this.loading = false;
    this.$nextTick(() => this.initMap());
    window.addEventListener('resize', this.handleMapResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleMapResize);
    if (this._mapResizeTimer) clearTimeout(this._mapResizeTimer);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'Tool', params: { toolId: this.toolId } });
    },
    handleMapResize() {
      this.isMobileViewport = window.innerWidth < 768;
      if (!this.map) return;
      if (this._mapResizeTimer) clearTimeout(this._mapResizeTimer);
      this._mapResizeTimer = setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 100);
    },
    async fetchTool() {
      try {
        const response = await api.get(`/Tool/${this.toolId}`);
        this.tool = response.data;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
      }
    },
    async fetchTraces() {
      try {
        const params = { sourceFilter: this.sourceFilter };
        if (this.dateRange && this.dateRange[0]) {
          params.from = `${this.dateRange[0]}T00:00:00`;
        }
        if (this.dateRange && this.dateRange[1]) {
          params.to = `${this.dateRange[1]}T23:59:59`;
        }
        const response = await api.get(`/GeoTrace/ForTool/${this.toolId}`, { params });
        this.traces = response.data;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
    async onFiltersChange() {
      this.loading = true;
      this.activeTraceKey = null;
      // Tear down existing map so it rebuilds for the new dataset
      if (this.map) {
        this.map.remove();
        this.map = null;
        this.markers = {};
      }
      await this.fetchTraces();
      this.loading = false;
      this.$nextTick(() => this.initMap());
    },
    iconFor(trace) {
      if (trace.source === SOURCE.Device) return 'sensors';
      return 'qr_code_2';
    },
    iconClassFor(trace) {
      if (trace.source === SOURCE.Device) return 'trace-icon-device';
      if (trace.source === SOURCE.ExternalToolReservation) return 'trace-icon-external';
      return 'trace-icon-reservation';
    },
    titleFor(trace) {
      if (trace.source === SOURCE.Device) {
        const dt = trace.deviceTypeName || this.$t('tools.deviceType');
        return `${dt} — ${trace.deviceId}`;
      }
      if (trace.source === SOURCE.ExternalToolReservation) {
        return trace.externalUserName || trace.reservationUserName || this.$t('tools.externalReservation');
      }
      return trace.reservationUserName || this.$t('tools.reservation');
    },
    subtitleFor(trace) {
      if (trace.source === SOURCE.Device) {
        return trace.address || this.$t('tools.gpsDeviceTrace');
      }
      const action = trace.reservationActionWasReserve ? this.$t('tools.reserved') : this.$t('tools.returned');
      const project = trace.reservationProjectName ? ` — ${trace.reservationProjectName}` : '';
      const kind = trace.source === SOURCE.ExternalToolReservation
        ? this.$t('tools.loanRentScan') || this.$t('tools.externalReservation')
        : this.$t('tools.reservationScan') || this.$t('tools.reservation');
      return `${kind} · ${action}${project}`;
    },
    formatTimestamp(ts) {
      if (!ts) return '';
      try {
        return new Date(ts).toLocaleString(appLocale());
      } catch {
        return ts;
      }
    },
    initMap() {
      const el = document.getElementById('toolGeoTracesMap');
      if (!el) return;
      this.map = L.map('toolGeoTracesMap');
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(this.map);

      const points = this.traces.filter(t => t.latitude != null && t.longitude != null);
      if (points.length === 0) {
        this.map.setView([65.6835, -18.1009], 5);
        return;
      }

      points.forEach(t => {
        const marker = L.marker([t.latitude, t.longitude], { icon: this.markerIconFor(t) })
          .addTo(this.map)
          .bindPopup(this.popupHtml(t));
        this.markers[`${t.source}-${t.id}`] = marker;
      });

      if (points.length === 1) {
        this.map.setView([points[0].latitude, points[0].longitude], 15);
      } else {
        const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
        this.map.fitBounds(bounds.pad(0.2));
      }
    },
    markerIconFor(trace) {
      const colorClass = trace.source === SOURCE.Device
        ? 'trace-marker-device'
        : (trace.source === SOURCE.ExternalToolReservation ? 'trace-marker-external' : 'trace-marker-reservation');
      const iconName = this.iconFor(trace);
      return L.divIcon({
        className: 'tool-geotrace-marker',
        html: `<div class="marker-bubble ${colorClass}"><i class="material-icons">${iconName}</i></div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
    },
    popupHtml(trace) {
      const title = this.escape(this.titleFor(trace));
      const subtitle = this.escape(this.subtitleFor(trace));
      const ts = this.escape(this.formatTimestamp(trace.timestamp));
      return `<div><strong>${title}</strong><br>${subtitle}<br><small>${ts}</small></div>`;
    },
    escape(s) {
      if (s == null) return '';
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    traceKey(trace) {
      return `${trace.source}-${trace.id}`;
    },
    isActive(trace) {
      return this.activeTraceKey === this.traceKey(trace);
    },
    highlightTrace(trace) {
      const key = this.traceKey(trace);
      const m = this.markers[key];
      if (!m || !this.map) return;
      this.activeTraceKey = key;
      m.openPopup();
    },
    focusOnTrace(trace) {
      const key = this.traceKey(trace);
      const m = this.markers[key];
      if (!m || !this.map) return;
      this.activeTraceKey = key;
      this.map.setView(m.getLatLng(), 16);
      m.openPopup();
    },
  },
};
</script>

<style scoped>
.map-big {
  height: 420px;
  width: 100%;
}

@media (min-width: 992px) {
  .map-big {
    height: 600px;
  }
  .trace-list {
    max-height: 600px;
    overflow-y: auto;
  }
}

.min-width-0 {
  min-width: 0;
}

.trace-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #fff;
  flex-shrink: 0;
}

.trace-icon i {
  font-size: 20px;
}

.trace-icon-reservation {
  background-color: #5e72e4;
}

.trace-icon-external {
  background-color: #fb6340;
}

.trace-icon-device {
  background-color: #2dce89;
}

.trace-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.trace-row:hover,
.trace-row.active {
  background-color: rgba(94, 114, 228, 0.08);
}

.range-picker-row .form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #344767;
}

.range-picker-row :deep(.el-date-editor) {
  --el-date-editor-daterange-width: 280px;
  width: auto;
  min-width: 280px;
}

.range-picker-row :deep(.el-radio-button__inner) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.trace-filter-icon {
  font-size: 16px;
  vertical-align: middle;
}

/* Mobile: stack controls vertically and let inputs take the full card width */
@media (max-width: 575.98px) {
  .range-picker-row {
    flex-direction: column;
    align-items: stretch;
  }
  .range-picker-row > .d-flex {
    flex-direction: column;
    align-items: stretch !important;
    gap: 4px !important;
  }
  .range-picker-row :deep(.el-date-editor) {
    width: 100% !important;
    min-width: 0;
  }
  .range-picker-row :deep(.el-radio-group) {
    display: flex;
    width: 100%;
  }
  .range-picker-row :deep(.el-radio-group .el-radio-button) {
    flex: 1 1 0;
    min-width: 0;
  }
  .range-picker-row :deep(.el-radio-group .el-radio-button__inner) {
    width: 100%;
    justify-content: center;
    padding-left: 8px;
    padding-right: 8px;
  }
  .range-picker-row .ms-auto {
    margin-left: 0 !important;
    text-align: right;
  }
}
</style>

<style>
.tool-geotrace-marker .marker-bubble {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}

.tool-geotrace-marker .marker-bubble i {
  font-size: 20px;
}

.tool-geotrace-marker .trace-marker-reservation {
  background-color: #5e72e4;
}

.tool-geotrace-marker .trace-marker-external {
  background-color: #fb6340;
}

.tool-geotrace-marker .trace-marker-device {
  background-color: #2dce89;
}

/* Date range picker popper — ensure it stacks above the sidebar */
.tool-geotraces-date-popper.el-popper {
  z-index: 9999 !important;
}

/* Mobile: keep the popup inside the viewport */
@media (max-width: 767.98px) {
  .tool-geotraces-date-popper.el-popper {
    max-width: calc(100vw - 16px) !important;
    width: auto !important;
  }
  .tool-geotraces-date-popper .el-picker-panel__sidebar {
    width: 100px !important;
  }
  .tool-geotraces-date-popper .el-picker-panel__body {
    margin-left: 100px !important;
  }
  .tool-geotraces-date-popper .el-picker-panel__shortcut {
    padding-left: 8px !important;
    font-size: 12px !important;
  }
}
</style>
